import { useState, useEffect, useCallback } from 'react';
import { Project } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { captureException } from '../utils/captureException';

export interface ProjectSearchResult {
  query: string;
  total_found: number;
  results: Project[];
}

export interface UseSearchProjectsResult {
  projects: Project[] | null;
  loading: boolean;
  error: string | null;
  preQuery: string;
  totalFound: number;
  searchQuery: string;
  refetch: (searchQuery: string) => void;
  clearResults: () => void;
}

export const useSearchProjects = (): UseSearchProjectsResult => {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [doFetch, setDoFetch] = useState(false);
  const [preQuery, setPreQuery] = useState<string>('');
  const [totalFound, setTotalFound] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    if (doFetch) {
      setPreQuery(searchQuery);
      fetchSearchResults(searchQuery);
    }
  }, [doFetch, searchQuery]);

  const fetchSearchResults = useCallback(async (searchQuery: string) => {
    if (doFetch === false) {
      return;
    }

    if (searchQuery === '') {
      setProjects(null);
      setTotalFound(0);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      let url: string;
      
      // Use the new contains search endpoint
      url = `${process.env.REACT_APP_API_URL || ''}/api/projects/search/${encodeURIComponent(searchQuery.trim())}`;
      
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      let transformedProjects: Project[] = [];
      
      // Handle contains search response (multiple projects)
      const searchResult: ProjectSearchResult = data;
      transformedProjects = searchResult.results.map((item: any) => ({
        project_code: item.project_code,
        fecha_entrega: item.fecha_entrega,
        contributors: item.contributors,
        "CONTROL CAMBIOS": item["CONTROL CAMBIOS"],
        "GARANTIA E IDIOMA": item["GARANTIA E IDIOMA"],
        "DETALLES DE LA ORDEN": item.DETALLES_DE_LA_ORDEN,
        "DETALLES DEL CHASIS": item.DETALLES_DEL_CHASIS,
        "DETALLES EQUIPO": item.DETALLES_EQUIPO,
        status_inspeccion: item.status_inspeccion,
        status_fotos: item.status_fotos,
        status_opcionales: item.status_opcionales,
        status_mediciones: item.status_mediciones,
        status_series: item.status_series,
        warning_series: item.warning_series,
        warning_mediciones: item.warning_mediciones,
        warning_opcionales: item.warning_opcionales,
        warning_fotos: item.warning_fotos,
        warning_inspeccion: item.warning_inspeccion,
        createdAt: item.createdAt,
        status: item.status,
        createdBy: item.createdBy,
        lastModifiedBy: item.lastModifiedBy,
        lastModifiedAt: item.lastModifiedAt,
        serial_code: item.serial_code,
        ficha_rosroca: item.ficha_rosroca,
        chasis_rosroca: item.chasis_rosroca,
        xapa_rosroca: item.xapa_rosroca,
        fotos: item.fotos,
        cover_photo: item.cover_photo,
        cover_thumbnail_photo: item.cover_thumbnail_photo,
        gcs_pdf_path: item.gcs_pdf_path,
        pdf_name: item.pdf_name,
        gcs_report_path: item.gcs_report_path,
        gcs_image_book_path: item.gcs_image_book_path,
        gcs_homologation_book_path: item.gcs_homologation_book_path,
        gcs_series_book_path: item.gcs_series_book_path,
        revision: item.revision,
        verified_by: item.verified_by,
      }));
      setTotalFound(searchResult.total_found);
      // sort projects by last modified at ascending
      transformedProjects.sort((a, b) => {
        return new Date(a.lastModifiedAt).getTime() - new Date(b.lastModifiedAt).getTime();
      });
      setProjects(transformedProjects);
      
    } catch (err) {
      captureException(err, { tags: { feature: 'useSearchProjects' }, extra: { action: 'searchProjects', searchQuery } });
      console.error('Error searching projects:', err);
      setError(err instanceof Error ? err.message : 'Error al buscar proyectos');
      setProjects(null);
      setTotalFound(0);
    } finally {
      setLoading(false);
    }
  }, [doFetch, searchQuery, user]);

  useEffect(() => {
    setDoFetch(true);
  }, [searchQuery]);

  const clearResults = useCallback(() => {
    setProjects(null);
    setTotalFound(0);
    setSearchQuery('');
    setError(null);
  }, []);

  return {
    projects,
    loading,
    error,
    preQuery,
    totalFound,
    searchQuery,
    refetch: (searchQuery: string) => setSearchQuery(searchQuery),
    clearResults,
  };
};
